import { useMemo } from 'react';

import { pdf } from '@react-pdf/renderer';
import PropTypes from 'prop-types';

import useEnv from '@zenbusiness/application-commons-env';
import Box from '@zenbusiness/zui/Box';
import Text from '@zenbusiness/zui/Text';

import * as ROUTES from '#client/constants/routes';
import { useCustomForm } from '#client/contexts/CustomFormContext';

import * as Styled from './BusinessCostResults.style';
import PoweredByVelo from '../PoweredByVelo/PoweredByVelo';
import ProfilePanel from '../ReportLayout/ProfilePanel';
import { ReportPanel } from '../ReportLayout/ReportPanel';
import SidePanel from '../ReportLayout/SidePanel';
import { BUSINESS_COST_SECTIONS } from '../utils/constants';
import BusinessCostResultPDF from './components/BusinessCostResultPDF/BusinessCostResultPDF';

/**
 * Business Cost Results component
 * Displays cost estimates for starting a business, including formation, licenses, permits, and taxes
 * Uses shared ReportPanel for consistent reporting UI
 *
 * @param {object} props - Component props
 * @param {object} props.data - Business cost results data containing costReport
 * @param {Function} [props.onComponentClick] - Callback for component actions
 * @component
 */
function BusinessCostResults({ data, onComponentClick }) {
  const { DEPLOY_ENV } = useEnv('head-first');
  const { isLoading, setLoading, resetForm, clearAnswers } = useCustomForm();

  const costReport = useMemo(() => data?.costReport || {}, [data?.costReport]);

  const handleDownloadReport = async () => {
    if (isLoading) return;

    setLoading(true);

    try {
      const blob = await pdf(
        <BusinessCostResultPDF environment={DEPLOY_ENV} report={costReport} />
      ).toBlob();
      const url = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = `Business_Cost_Report.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('[BusinessCostResults] Download report error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartOver = async () => {
    if (isLoading) return;

    setLoading(true);
    if (onComponentClick) {
      await resetForm();
      await clearAnswers();
      await onComponentClick({ type: 'business_cost_start_over' });
    }
    setLoading(false);
  };

  // Handle case where no cost report is provided
  if (!costReport || !costReport.id) {
    return (
      <Styled.BusinessCostResultsRoot>
        <Box style={{ textAlign: 'center' }}>
          <Text>No cost estimates found</Text>
          <Text>
            We weren&apos;t able to generate cost estimates based on your responses. Please try
            again.
          </Text>
        </Box>
      </Styled.BusinessCostResultsRoot>
    );
  }

  return (
    <Styled.BusinessCostResultsRoot>
      <Styled.ReportHeader>
        <Text size="md" weight="medium">
          Your Business Cost Estimator
        </Text>
      </Styled.ReportHeader>
      <Styled.ReportContent>
        <SidePanel
          profilePanel={
            <ProfilePanel
              clickstreamElementName="business_cost_start_over"
              fields={[
                {
                  key: 'businessType',
                  label: 'Type',
                  value: costReport.userInput?.businessType
                },
                {
                  key: 'location',
                  label: 'State',
                  value: costReport.userInput?.location
                },
                {
                  key: 'businessIndustry',
                  label: 'Industry',
                  value: costReport.userInput?.businessIndustry
                },
                ...(costReport.userInput?.citiesOfOperation
                  ? [
                      {
                        key: 'citiesOfOperation',
                        label: 'Cities of Operation',
                        useMarkdown: true,
                        value: costReport.userInput.citiesOfOperation
                      }
                    ]
                  : [])
              ]}
              loading={isLoading}
              startOverText="Start over"
              onStartOver={handleStartOver}
            />
          }
        />
        <ReportPanel
          iconVariant="rounded"
          loading={isLoading}
          reportData={costReport}
          sectionsConfig={BUSINESS_COST_SECTIONS}
          onDownloadReport={handleDownloadReport}
        />
      </Styled.ReportContent>
      <PoweredByVelo url={ROUTES.website(DEPLOY_ENV)} />
    </Styled.BusinessCostResultsRoot>
  );
}

BusinessCostResults.propTypes = {
  data: PropTypes.object.isRequired,
  onComponentClick: PropTypes.func
};

export default BusinessCostResults;
